/* Gestão do admin-push de notificações no backoffice (ADR-0687). Lista (todos os tiers) paginada por
   cursor keyset com "Carregar mais", compõe/publica/remove (super_admin+support). O app puxa as
   `published` no launch + realtime.
   Layout de lista padronizado (ADR-0702/0704): busca por título + filtros por status/severidade/
   público/expiração. Com paginação os filtros são SERVER-SIDE (a página é o único recorte que chega —
   filtrar client-side esconderia itens de páginas ainda não carregadas). */
import type {
  StaffRole,
  UserNotification,
  UserNotificationAudience,
  UserNotificationExpiryFilter,
  UserNotificationStatus,
} from '@planuze/admin-contracts';
import {
  NOTIFICATION_BULK_MAX_IDS,
  USER_NOTIFICATION_EXPIRY_FILTERS,
  USER_NOTIFICATION_SEVERITIES,
  USER_NOTIFICATION_STATUSES,
} from '@planuze/admin-contracts';
import {
  Button,
  ConfirmDialog,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  TableSkeleton,
  toast,
} from '@planuze/ui';
import { useState } from 'react';
import { FacetMultiSelect } from '../components/filters/facet-multi-select.js';
import { SegmentedControl, type SegmentOption } from '../components/filters/segmented-control.js';
import { NotificationBulkBar } from '../components/notifications/notification-bulk-bar.js';
import { NotificationComposeModal } from '../components/notifications/notification-compose-modal.js';
import { NotificationsTable } from '../components/notifications/notifications-table.js';
import { SearchInput } from '../components/search-input.js';
import { useNotificationsContent } from '../content/sections/notifications.js';
import {
  useAdminNotificationList,
  useBulkDeleteDraftNotifications,
  useDeleteAdminNotification,
  usePublishAdminNotification,
} from '../hooks/use-admin-notifications.js';
import { useDebounce } from '../hooks/use-debounce.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'support'];

type StatusFilter = 'all' | UserNotificationStatus;
/* `any` é o "sem filtro"; `all`/`users` são os valores reais de audience (evita colisão de rótulo). */
type AudienceFilter = 'any' | UserNotificationAudience;
type ExpiryFilter = 'all' | UserNotificationExpiryFilter;

export function NotificationsPage() {
  const copy = useNotificationsContent();
  /* Contadores por opção foram removidos: com paginação server-side a contagem global não é
     conhecida no client (só chegam as páginas carregadas). */
  const statusOptions: readonly SegmentOption<StatusFilter>[] = [
    { value: 'all', label: copy.allLabel },
    ...USER_NOTIFICATION_STATUSES.map((s) => ({ value: s, label: copy.statusLabels[s] })),
  ];
  const audienceOptions: readonly SegmentOption<AudienceFilter>[] = [
    { value: 'any', label: copy.audienceAnyLabel },
    { value: 'all', label: copy.audienceGeneralLabel },
    { value: 'users', label: copy.audienceTargetedLabel },
  ];
  const expiryOptions: readonly SegmentOption<ExpiryFilter>[] = [
    { value: 'all', label: copy.expiryAllLabel },
    ...USER_NOTIFICATION_EXPIRY_FILTERS.map((e) => ({ value: e, label: copy.expiryLabels[e] })),
  ];
  const session = useStaffSession();
  const canManage = session !== null && MANAGE_ROLES.includes(session.role);
  const publish = usePublishAdminNotification();
  const del = useDeleteAdminNotification();
  const bulkDelete = useBulkDeleteDraftNotifications();
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [severities, setSeverities] = useState<string[]>([]);
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>('any');
  const [expiryFilter, setExpiryFilter] = useState<ExpiryFilter>('all');
  /* Confirmação destrutiva via Dialog (§5/§8) — antes o delete disparava no clique só com toast. */
  const [toDelete, setToDelete] = useState<UserNotification | null>(null);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [pendingBulkIds, setPendingBulkIds] = useState<readonly string[] | null>(null);

  const debouncedSearch = useDebounce(search.trim(), 300);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAdminNotificationList({
      search: debouncedSearch.length > 0 ? debouncedSearch : undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      severities: severities.length > 0 ? severities : undefined,
      audience: audienceFilter === 'any' ? undefined : audienceFilter,
      expiry: expiryFilter === 'all' ? undefined : expiryFilter,
    });
  const items = data?.pages.flatMap((page) => page.items) ?? [];
  /* Publicadas permanecem inelegíveis na UI e no service. A interseção com os itens visíveis
     impede que uma troca de filtro envie rascunhos que saíram da tela. */
  const eligibleItems = items.filter((notification) => notification.status === 'draft');
  const selectedItems = eligibleItems.filter((notification) => selectedIds.has(notification.id));

  const toggle = (id: string): void =>
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else if (next.size < NOTIFICATION_BULK_MAX_IDS) next.add(id);
      return next;
    });

  const toggleAll = (checked: boolean): void =>
    setSelectedIds(
      checked
        ? new Set(
            eligibleItems
              .slice(0, NOTIFICATION_BULK_MAX_IDS)
              .map((notification) => notification.id),
          )
        : new Set(),
    );

  const onPublish = (n: UserNotification): void => {
    publish.mutate(n.id, {
      onSuccess: () => toast.success(copy.publishSuccessToast),
      onError: () => toast.error(copy.publishErrorToast),
    });
  };

  const hasFilters =
    debouncedSearch.length > 0 ||
    statusFilter !== 'all' ||
    severities.length > 0 ||
    audienceFilter !== 'any' ||
    expiryFilter !== 'all';
  const clearFilters = (): void => {
    setSearch('');
    setStatusFilter('all');
    setSeverities([]);
    setAudienceFilter('any');
    setExpiryFilter('all');
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        {canManage && (
          <PageActions>
            <Button onClick={() => setModalOpen(true)}>{copy.newNotificationButton}</Button>
          </PageActions>
        )}
      </PageHeader>
      <PageContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            ariaLabel={copy.searchAriaLabel}
            placeholder={copy.searchPlaceholder}
          />
          <SegmentedControl
            value={statusFilter}
            onChange={setStatusFilter}
            options={statusOptions}
            ariaLabel={copy.statusFilterAriaLabel}
          />
          <FacetMultiSelect
            label={copy.severityFacetLabel}
            values={severities}
            onChange={setSeverities}
            options={USER_NOTIFICATION_SEVERITIES.map((s) => ({
              value: s,
              label: copy.severityLabels[s],
            }))}
          />
          <SegmentedControl
            value={audienceFilter}
            onChange={setAudienceFilter}
            options={audienceOptions}
            ariaLabel={copy.audienceFilterAriaLabel}
          />
          <SegmentedControl
            value={expiryFilter}
            onChange={setExpiryFilter}
            options={expiryOptions}
            ariaLabel={copy.expiryFilterAriaLabel}
          />
        </div>
        {canManage && (
          <NotificationBulkBar
            selectedCount={selectedItems.length}
            busy={bulkDelete.isPending}
            onDelete={() =>
              setPendingBulkIds(
                selectedItems
                  .slice(0, NOTIFICATION_BULK_MAX_IDS)
                  .map((notification) => notification.id),
              )
            }
            onClear={() => setSelectedIds(new Set())}
          />
        )}
        {isError ? (
          <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>
        ) : isLoading ? (
          <TableSkeleton columns={canManage ? 6 : 4} />
        ) : (
          <NotificationsTable
            items={items}
            canManage={canManage}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
            onCompose={() => setModalOpen(true)}
            onPublish={onPublish}
            onDelete={setToDelete}
            publishPending={publish.isPending}
            deletePending={del.isPending}
            selectedIds={selectedIds}
            onToggle={toggle}
            onToggleAll={toggleAll}
          />
        )}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              loading={isFetchingNextPage}
              onClick={() => {
                fetchNextPage().catch(() => undefined);
              }}
            >
              {copy.loadMoreButton}
            </Button>
          </div>
        )}
      </PageContent>
      <NotificationComposeModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => {
          if (!open) setToDelete(null);
        }}
        title={copy.deleteDialogTitle}
        description={toDelete !== null ? copy.deleteDialogDescription(toDelete.title) : undefined}
        confirmLabel={copy.deleteConfirmButton}
        variant="destructive"
        onConfirm={async () => {
          const n = toDelete;
          if (n === null) return;
          try {
            await del.mutateAsync(n.id);
            toast.success(copy.deleteSuccessToast);
          } catch {
            toast.error(copy.deleteErrorToast);
          }
          setToDelete(null);
        }}
      />
      <ConfirmDialog
        open={pendingBulkIds !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBulkIds(null);
        }}
        title={copy.bulkTitle}
        description={copy.bulkDescription(pendingBulkIds?.length ?? 0)}
        confirmLabel={copy.bulkConfirm}
        variant="destructive"
        mapError={() => copy.bulkError}
        onConfirm={async () => {
          if (pendingBulkIds === null || pendingBulkIds.length === 0) return;
          const response = await bulkDelete.mutateAsync(pendingBulkIds);
          const failed = response.results.filter((result) => !result.ok);
          if (failed.length === 0) toast.success(copy.bulkSuccess(response.results.length));
          else {
            toast.warning(copy.bulkPartial(response.results.length - failed.length, failed.length));
          }
          setSelectedIds(new Set(failed.map((result) => result.id)));
          setPendingBulkIds(null);
        }}
      />
    </Page>
  );
}
